import { computed, Injectable, signal } from '@angular/core';
import { IEstabelecimentoClienteEntity } from '@nomoh-monorepo/shared-types';

/**
 * Data Service para Cliente do Estabelecimento - Gestão de estado com Signals
 */
@Injectable({
  providedIn: 'root',
})
export class ClienteDataService {
  // State privado
  private _clientes = signal<IEstabelecimentoClienteEntity[]>([]);
  private _clienteAtual = signal<IEstabelecimentoClienteEntity | null>(null);
  private _total = signal<number>(0);
  private _loading = signal(false);
  private _erro = signal<string | null>(null);

  // Signals públicos (readonly)
  $clientes = this._clientes.asReadonly();
  $clienteAtual = this._clienteAtual.asReadonly();
  $total = this._total.asReadonly();
  $loading = this._loading.asReadonly();
  $erro = this._erro.asReadonly();

  // Computed signals
  $totalClientesLoaded = computed(() => this._clientes().length);

  // Setters
  setClientes(clientes: IEstabelecimentoClienteEntity[], total: number) {
    this._clientes.set(this.normalizarClientes(clientes));
    this._total.set(total);
  }

  setClienteAtual(cliente: IEstabelecimentoClienteEntity | null) {
    this._clienteAtual.set(cliente);
  }

  addCliente(cliente: IEstabelecimentoClienteEntity) {
    this._clientes.update((current) =>
      this.normalizarClientes([...current, cliente])
    );
    this._total.update((curr) => curr + 1);
  }

  updateCliente(id: string, data: Partial<IEstabelecimentoClienteEntity>) {
    this._clientes.update((current) =>
      this.normalizarClientes(
        current.map((c) => (c.id === id ? { ...c, ...data } : c))
      )
    );

    if (this._clienteAtual()?.id === id) {
      this._clienteAtual.update((current) =>
        current ? { ...current, ...data } : null
      );
    }
  }

  removeCliente(id: string) {
    this._clientes.update((current) =>
      this.normalizarClientes(current.filter((c) => c.id !== id))
    );
    this._total.update((curr) => (curr > 0 ? curr - 1 : 0));

    if (this._clienteAtual()?.id === id) {
      this._clienteAtual.set(null);
    }
  }

  setLoading(loading: boolean) {
    this._loading.set(loading);
  }

  setErro(erro: string | null) {
    this._erro.set(erro);
  }

  limparErro() {
    this._erro.set(null);
  }

  limpar() {
    this._clientes.set([]);
    this._clienteAtual.set(null);
    this._total.set(0);
    this._loading.set(false);
    this._erro.set(null);
  }

  private normalizarClientes(
    clientes: IEstabelecimentoClienteEntity[]
  ): IEstabelecimentoClienteEntity[] {
    return [...(clientes || [])].sort((a, b) => a.nome.localeCompare(b.nome));
  }
}
